/**
 * API COVERAGE — SubToursController (/api/places/sub-tours): Module 1 sub-tours (S1–S4);
 * RoutesController (/api/places/routes, /api/places/travel-defaults): travel times (R1–R2).
 */
import { api, http } from '@/lib/apiClient';
import type { BulkResult, NamedRef, PlaceStatus, Skipped } from './places.api';
import type { PoiImportCheck, PoiImportRow, Translation } from './pois.api';

// ---------------- Sub-tours ----------------

export interface SubTourStop { poiId: number; minutes: number }
export interface SubTourStopView extends SubTourStop { code: string; name: string; kind: string; vehicleAccess: 'All' | 'Small' | 'None' }
export interface RateExtra { item: string; amount: number; per: 'vehicle' | 'day' }
export interface SubTourRate {
  option: string; validFrom?: string | null; validTo?: string | null; adultRate?: number | null; childRate?: number | null;
  childAgeFrom?: number | null; childAgeTo?: number | null; vehicleRate?: number | null;
  tolls: boolean; parking: boolean; driverAllowance: boolean; stateTax: boolean; extras?: RateExtra[] | null;
}
export interface GuideRate { language: string; ratePerGroup: number }
export interface AddonVehiclePrice { vehicleType: string; amount: number }
export type RateBasis = 'Per person' | 'Per vehicle' | 'Per group';
export interface SubTourAddon {
  name: string; description: string; rateBasis: RateBasis; price?: number | null; childPrice?: number | null;
  vehiclePrices?: AddonVehiclePrice[] | null; validFrom?: string | null; validTo?: string | null; permitNeeded: boolean;
  permitNote?: string | null; poiId?: number | null;
}

export interface SubTourListItem {
  id: number; code: string; name: string; type: string; baseDestinationId: number; destination: string; transportOptions: string[];
  addons: number; stops: number; status: PlaceStatus;
}
export interface SubTourList { items: SubTourListItem[]; total: number; all: number }

export interface SubTour {
  id: number; code: string; name: string; subTourType: string; baseDestinationId: number; baseDestinationName: string;
  toDestinationId?: number | null; toDestinationName?: string | null; durationMinutes: number; startTimes: string[]; operatingDays: number[];
  seasonMonths: number[]; dayText?: string | null; stops: SubTourStopView[]; transportOptions: string[]; rates: SubTourRate[];
  guideAvailable: boolean; guides: GuideRate[]; addons: SubTourAddon[]; entryFeesIncluded: boolean; shortDescription?: string | null;
  longDescription?: string | null; coverFileId?: number | null; galleryFileIds: number[]; tags: string[]; translations: Translation[];
  status: PlaceStatus; warnings: string[]; vehicleAccessNote: string; supplierVendorId?: number | null; supplierName?: string | null;
}

export interface SaveSubTour {
  name: string; subTourType: string; baseDestinationId: number; toDestinationId?: number | null; durationMinutes: number;
  startTimes: string[]; operatingDays: number[]; seasonMonths: number[]; dayText?: string | null; stops: SubTourStop[];
  transportOptions: string[]; rates: SubTourRate[]; guideAvailable: boolean; guides: GuideRate[]; addons: SubTourAddon[];
  entryFeesIncluded: boolean; shortDescription?: string | null; longDescription?: string | null; coverFileId?: number | null;
  galleryFileIds: number[]; tags: string[]; translations: Translation[]; status?: 'Active' | 'Inactive' | null; confirmNearDuplicate?: boolean;
  /** The vendor who runs it (Module 2). */
  supplierVendorId?: number | null;
}

export interface SubTourListParams { search?: string; type?: string; destinationId?: number; transport?: string; status?: string; page?: number; pageSize?: number }

async function download(url: string, body: unknown | undefined, fallback: string) {
  const res = body === undefined ? await http.get(url, { responseType: 'blob' }) : await http.post(url, body, { responseType: 'blob' });
  const name = /filename="?([^";]+)"?/i.exec(res.headers['content-disposition'] ?? '')?.[1] ?? fallback;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
}

/** The import endpoints every places master shares (sample, check, import, undo). */
export interface ImportApi {
  sample: () => Promise<void>;
  checkImport: (file: File) => Promise<PoiImportCheck>;
  importRows: (fileName: string, rows: PoiImportRow[], skipErrorRows: boolean) => Promise<{ batchId: number; imported: number; skipped: number; message: string }>;
  undoImport: (batchId: number) => Promise<string>;
}

function importApi(base: string, sampleName: string): ImportApi {
  return {
    sample: () => download(`${base}/sample`, undefined, sampleName),
    checkImport: (file: File) => { const f = new FormData(); f.append('file', file); return api.post<PoiImportCheck>(`${base}/import/check`, f); },
    importRows: (fileName, rows, skipErrorRows) =>
      api.post<{ batchId: number; imported: number; skipped: number; message: string }>(`${base}/import`, { fileName, rows, skipErrorRows }),
    undoImport: (batchId) => api.post<string>(`${base}/import/${batchId}/undo`),
  };
}

const sb = '/places/sub-tours';
export const subToursApi = {
  list: (p: SubTourListParams) => api.get<SubTourList>(sb, p as Record<string, unknown>),
  options: (destinationId?: number) => api.get<NamedRef[]>(`${sb}/options`, { destinationId }),
  get: (id: number) => api.get<SubTour>(`${sb}/${id}`),
  create: (b: SaveSubTour) => api.post<SubTour>(sb, b),
  update: (id: number, b: SaveSubTour) => api.put<SubTour>(`${sb}/${id}`, b),
  check: (b: SaveSubTour) => api.post<string[]>(`${sb}/check`, b),
  setStatus: (ids: number[], status: 'Active' | 'Inactive') => api.post<BulkResult>(`${sb}/status`, { ids, status }),
  deletePreview: (ids: number[]) => api.post<{ willDelete: NamedRef[]; willSkip: Skipped[] }>(`${sb}/delete-preview`, { ids }),
  remove: (ids: number[]) => api.post<BulkResult>(`${sb}/delete`, { ids }),
  exportExcel: (body: { ids?: number[] } & SubTourListParams) => download(`${sb}/export`, body, 'subtours.xlsx'),
  ...importApi(sb, 'subtours_sample.xlsx'),
};

// ---------------- Travel times ----------------

export interface RouteClosure { from: string; to: string; reason?: string | null; status: 'Expected' | 'Confirmed' }

export interface RouteListItem {
  id: number; code: string; fromId: number; from: string; toId: number; to: string; sameBothWays: boolean; distanceKm: number;
  summerMinutes: number; winterMinutes?: number | null; closedInWinter: boolean; closures: string[]; source: 'Maps' | 'Manual';
  checked: boolean; status: PlaceStatus;
}
export interface RouteList { items: RouteListItem[]; total: number; all: number; uncheckedFromMaps: number }

export interface Route {
  id: number; code: string; fromDestinationId: number; fromName: string; toDestinationId: number; toName: string; sameBothWays: boolean;
  distanceKm: number; summerMinutes: number; winterMinutes?: number | null; winterFrom?: string | null; winterTo?: string | null;
  routeVia?: string | null; roadNotes?: string | null; permitRequired: boolean; permitDetails?: string | null; closures: RouteClosure[];
  alternateRouteId?: number | null; alternateRouteLabel?: string | null; alternateText?: string | null; alternateKm?: number | null;
  alternateMinutes?: number | null; source: 'Maps' | 'Manual'; checked: boolean; status: PlaceStatus;
}

export type SaveRoute = Omit<Route, 'id' | 'code' | 'fromName' | 'toName' | 'alternateRouteLabel' | 'checked' | 'status'> & { status?: 'Active' | 'Inactive' | null };

export interface RouteDay {
  date: string; minutes?: number | null; season: 'Summer' | 'Winter'; closed: boolean; closureText?: string | null; alternateLabel?: string | null;
  alternateKm?: number | null; alternateMinutes?: number | null; overnightSuggested: boolean; overnightStop?: string | null; message: string;
}

export interface TravelDefaults { vehicleAccessNote: string; maxDriveHoursPerDay: number }

export interface RouteListParams { search?: string; hasClosure?: boolean; source?: string; status?: string; page?: number; pageSize?: number }

const rb = '/places/routes';
export const routesApi = {
  list: (p: RouteListParams) => api.get<RouteList>(rb, p as Record<string, unknown>),
  options: (exceptId?: number) => api.get<NamedRef[]>(`${rb}/options`, { exceptId }),
  get: (id: number) => api.get<Route>(`${rb}/${id}`),
  plan: (id: number, date: string, reverse = false) => api.get<RouteDay>(`${rb}/${id}/plan`, { date, reverse }),
  mapEstimate: (fromId: number, toId: number) => api.get<{ distanceKm: number; minutes: number }>(`${rb}/map-estimate`, { fromId, toId }),
  create: (b: SaveRoute) => api.post<Route>(rb, b),
  update: (id: number, b: SaveRoute) => api.put<Route>(`${rb}/${id}`, b),
  setStatus: (ids: number[], status: 'Active' | 'Inactive') => api.post<BulkResult>(`${rb}/status`, { ids, status }),
  deletePreview: (ids: number[]) => api.post<{ willDelete: NamedRef[]; willSkip: Skipped[] }>(`${rb}/delete-preview`, { ids }),
  remove: (ids: number[]) => api.post<BulkResult>(`${rb}/delete`, { ids }),
  exportExcel: (body: { ids?: number[] } & RouteListParams) => download(`${rb}/export`, body, 'travel_times.xlsx'),
  ...importApi(rb, 'travel_times_sample.xlsx'),
  defaults: () => api.get<TravelDefaults>('/places/travel-defaults'),
  saveDefaults: (b: TravelDefaults) => api.put<TravelDefaults>('/places/travel-defaults', b),
};

/** "8 h 30 m". */
export const hm = (minutes?: number | null) => (minutes == null ? '—' : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} m`);

/** "6 hours", "6.5 hours", "45 min". */
export const hoursText = (minutes: number) => (minutes < 60 ? `${minutes} min` : `${+(minutes / 60).toFixed(2)} hour${minutes === 60 ? '' : 's'}`);

/** "11-15" → "15 Nov". */
export function dayMonth(mmdd?: string | null) {
  if (!mmdd) return '';
  const [m, d] = mmdd.split('-').map(Number);
  if (!m || !d) return mmdd;
  return `${d} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]}`;
}
